import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["tableComponent", "rowAriaLabel", "allExpanded", "render", "renderRow", "expandedRows", "nonExpandableRows", "onExpandedChange", "onExpandedAllChange", "customStrings", "footerExpanded", "totalRows", "rowsCollapsed", "responsiveOnly", "noExpandAllButton", "disableExpandAllButton", "bodyRowComponent"],
      _excluded2 = ["expandable", "data"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useRef } from "react";
import { EXPANDABLE_COLUMN_KEY } from "../constants";
import ExpandableButton from "./ExpandableButton";
import ExpandableRowBodyRow from "./ExpandableRowBodyRow";
import extractTheme from "./extractTheme";
/**
 * `withExpandableRow()` High Order Component adds support for row-expansion
 * Table component.
 * It will always show the expand/collapse button in the
 * table and extra content will be shown below the row, when expanded.
 *
 * The resulting wrapped component will accept these extra props apart from
 * the ones from Table:
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
const ExpandableRow = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    tableComponent: TableComponent,
    rowAriaLabel,
    // eslint-disable-line no-unused-vars
    allExpanded,
    // eslint-disable-line no-unused-vars
    render,
    // eslint-disable-line no-unused-vars
    renderRow,
    // eslint-disable-line no-unused-vars
    expandedRows,
    // eslint-disable-line no-unused-vars
    nonExpandableRows,
    // eslint-disable-line no-unused-vars, react/prop-types
    onExpandedChange: onExpandedChangeProp,
    // eslint-disable-line no-unused-vars
    onExpandedAllChange: onExpandedAllChangeProp,
    // eslint-disable-line no-unused-vars
    customStrings,
    // eslint-disable-line no-unused-vars
    footerExpanded,
    // eslint-disable-line no-unused-vars, react/prop-types
    totalRows,
    // eslint-disable-line no-unused-vars
    rowsCollapsed,
    // eslint-disable-line no-unused-vars
    responsiveOnly,
    // eslint-disable-line no-unused-vars
    noExpandAllButton,
    // eslint-disable-line no-unused-vars, react/prop-types
    disableExpandAllButton,
    // eslint-disable-line no-unused-vars, react/prop-types
    bodyRowComponent
  } = _ref,
      tableProps = _objectWithoutProperties(_ref, _excluded);

  const internalRef = useRef();
  const tableRef = ref || internalRef;
  const theme = useTheme();
  const {
    expandableStyles
  } = extractTheme(_objectSpread({
    theme
  }, tableProps));
  /**
   * Handles expanded button click and calls the `onExpandedChange` callback function
   */

  const onExpandedChange = (itemKey, expanded) => {
    let newExpandedRows = [...expandedRows];

    if (expanded) {
      newExpandedRows = [...newExpandedRows, itemKey];
    } else {
      newExpandedRows.splice(newExpandedRows.indexOf(itemKey), 1);
    }

    onExpandedChangeProp({
      expanded,
      expandedItemKey: itemKey,
      expandedRows: newExpandedRows,
      // `id` is deprecated. Use `expandedItemKey` instead.
      id: itemKey
    });
  };
  /**
   * Handles expand all button click
   */


  const onExpandedAllChange = (itemKey, expanded) => {
    onExpandedAllChangeProp(expanded);
  };
  /**
   * Get the Accessibility label for a given input.
   *
   * @returns String
   */


  const getA11YLabel = (item, itemKey, isExpanded) => {
    if (rowAriaLabel) {
      return rowAriaLabel({
        item,
        expanded: isExpanded
      });
    }

    return customStrings.rowExpandButtonLabel(itemKey, {
      item,
      expanded: isExpanded
    });
  };
  /**
   * Get props to be returned in Table's `bodyRowComponentProps` prop.
   * @param Object Takes an object and expects to destruct `itemKey`.
   * @returns Returns an object of properties describing the state of the row.
   */


  const getExpandableRowProps = props => {
    let defaultRenderRow;
    const {
      itemKey
    } = props; // eslint-disable-line react/prop-types

    const nonExpandableRow = // eslint-disable-next-line react/prop-types
    nonExpandableRows && nonExpandableRows.indexOf(itemKey) >= 0; // eslint-disable-next-line react/prop-types

    const isExpanded = allExpanded || expandedRows.indexOf(itemKey) !== -1;
    return _objectSpread(_objectSpread({}, props), {}, {
      expanded: isExpanded,
      render,
      bodyRowComponent,
      // Make sure we pass `undefined` instead of `null` so we get the default.
      renderRow: renderRow || defaultRenderRow,
      contentStyles: expandableStyles.expandedContentStyles,
      customStrings,
      noExpandableRowBody: !rowsCollapsed && nonExpandableRow
    });
  };
  /**
   * Checks if the parameter is a String or a Function.
   * @param Function/String Takes function or string as an argument.
   * @returns  String, the accessible label.
   */


  const checkCustomStringType = customString => {
    return typeof customString === "function" ? customString(totalRows) : customString;
  };
  /**
   * Renders the 'Expand All' button
   */


  const renderExpandableHeaderContent = () => {
    const label = allExpanded ? checkCustomStringType(customStrings.collapseAll) : checkCustomStringType(customStrings.expandAll);
    return _jsx(ExpandableButton, {
      expandAll: true,
      expanded: allExpanded,
      onExpandedChange: onExpandedAllChange,
      "aria-label": label,
      css: expandableStyles.ExpandableButton,
      hiddenTextPlaceholder: totalRows,
      customStrings: customStrings,
      disabled: disableExpandAllButton
    });
  };
  /**
   * Renders the 'Expand' button for rows
   */


  const renderExpandableCellContent = props => {
    const {
      item,
      itemKey
    } = props; // eslint-disable-line react/prop-types

    const isExpanded = allExpanded || expandedRows.indexOf(itemKey) !== -1;
    const label = getA11YLabel(item, itemKey, isExpanded);
    /**
     * Not to render the expandable button for the row without expandable content.
     */

    const nonExpandableRow = // eslint-disable-next-line react/prop-types
    nonExpandableRows && nonExpandableRows.indexOf(itemKey) >= 0;
    return _jsx(React.Fragment, {
      children: (rowsCollapsed || !nonExpandableRow) && _jsx(ExpandableButton, {
        item: item,
        itemKey: itemKey,
        expanded: isExpanded,
        onExpandedChange: onExpandedChange,
        "aria-label": label,
        customStrings: customStrings,
        "aria-controls": isExpanded ? itemKey : null
      })
    });
  };

  const {
    columns: originalColumns
  } = tableProps;
  const columns = [{
    key: EXPANDABLE_COLUMN_KEY,
    label: " ",
    className: "pnr-table__col-expandable-toggle",
    header: renderExpandableHeaderContent,
    headerCellComponent: "td",
    formatter: renderExpandableCellContent
  }, ...originalColumns];
  return _jsx(TableComponent, _objectSpread(_objectSpread({}, tableProps), {}, {
    columns: columns,
    css: expandableStyles,
    customStrings: customStrings,
    bodyRowComponent: ExpandableRowBodyRow,
    bodyRowComponentProps: getExpandableRowProps,
    ref: tableRef
  }));
});
ExpandableRow.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The columns used to render the table, used for spanning total column length
   */
  columns: PropTypes.array.isRequired,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Override for strings used within the component
   */
  customStrings: PropTypes.shape({
    // Used for text 'Expand' on single row button in `Expandable` HOC
    expandSingle: PropTypes.string,
    // Used for text 'Collapse' on single row button in `Expandable` HOC
    collapseSingle: PropTypes.string,
    // Used for text 'Expand All' on header button in `Expandable` HOC
    expandAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    // Used for text 'Collapse All' on header button in `Expandable` HOC
    collapseAll: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    // Used for changing announcement text of 'Collapse Row' button in `Expandable` HOC.
    collapsedRowAnnouncement: PropTypes.func,
    // Used for changing announcement text of 'Expand Row' button in `Expandable` HOC.
    expandedRowAnnouncement: PropTypes.func,
    // Used for changing announcement text of 'Expand' button in `Expandable` HOC.
    expandAllRowsAnnouncement: PropTypes.func,
    // Used for changing announcement text of 'Collapse' button in `Expandable` HOC.
    collapseAllRowsAnnouncement: PropTypes.func,

    /**
     * Used for `more about ${itemKey}` text for
     * Expandable button in `Expandable` HOC.
     */
    rowExpandButtonLabel: PropTypes.func,

    /**
     * Used for overriding default text `${itemKey}, expanded content`
     * of Expanded content of a single row.
     */
    rowExpandedLabel: PropTypes.func,

    /**
     * Used for overriding default textContent `${itemKey} details`
     * of visually hidden node inside Expanded content of a single row.
     */
    rowExpandedDetails: PropTypes.func
  }).isRequired,

  /**
   * Event prop called when expanded all button is clicked. It will receive
   * an boolean value for all rows being expanded or collapsed
   */
  onExpandedAllChange: PropTypes.func.isRequired,

  /**
   * Event prop called when expanded state changes. It will receive
   * an object with these properties:
   * <ul>
   *  <li>
   *    `expanded`: Row's selected state.
   *  </li>
   *  <li>
   *    `expandedItemKey`: Returns the recently toggled row value as it relates to the `rowKey`.
   *  </li>
   *  <li>
   *    `expandedRows`: Array of expanded ids.
   *  </li>
   * </ul>
   */
  onExpandedChange: PropTypes.func.isRequired,

  /**
   * Render Prop responsible for returning the expandable row content.
   * It will be invoked every time an expandable row is expanded
   * @param {Boolean} item Row item
   */
  render: PropTypes.func.isRequired,

  /**
   * The Table component to be extended. Used by the High Order Component implementation
   * @ignore
   */
  tableComponent: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Boolean prop determining if all rows should be expanded.
   * This prop will override all rows to be expanded
   * All rows should still be passed into `expandedRows`
   */
  allExpanded: PropTypes.bool,

  /**
   * Array of rows to be expanded. Any rows included here will be expanded
   */
  expandedRows: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Render prop responsible for returning a custom expanded row.
   */
  renderRow: PropTypes.func,

  /**
   * Renders the row as expandable only if there are collapsed columns.
   */
  responsiveOnly: PropTypes.bool,

  /**
   * Render prop for the Checkbox/Radio aria-label
   * @param {Object} rowData
   * @param {Object} rowData.item Row's item data
   * @param {Boolean} rowData.expanded Row's expanded state
   */
  rowAriaLabel: PropTypes.func,

  /**
   * Indicates if any column is in collapsed state.
   */
  rowsCollapsed: PropTypes.bool,

  /**
   * Total rows in the data set.
   */
  totalRows: PropTypes.number
} : {};
ExpandableRow.defaultProps = {
  expandedRows: [],
  allExpanded: false,
  rowAriaLabel: null,
  totalRows: null,
  renderRow: null,
  rowsCollapsed: null,
  responsiveOnly: null
};
/**
 * High Order Component that will return a ExpandableRow component
 * which will internally use the passed Table class argument as the React element
 * @param  {React.Element} TableComponent The Table component or another wrapped
 *                                        Table component.
 * @return {Function}                     A Functional component that will enhance
 *                                        TableComponent
 */

function withExpandableRow(TableComponent) {
  const WrappedTable = /*#__PURE__*/React.forwardRef((props, ref) => {
    const {
      expandable,
      data
    } = props,
          others = _objectWithoutProperties(props, _excluded2);

    const {
      noExpandAllButton,
      responsiveOnly,
      rowsCollapsed
    } = expandable || {};

    if (expandable && data && data.length > 0 && (!responsiveOnly || responsiveOnly && rowsCollapsed) && !noExpandAllButton) {
      return _jsx(ExpandableRow, _objectSpread(_objectSpread(_objectSpread({
        data: data
      }, others), expandable), {}, {
        ref: ref,
        tableComponent: TableComponent
      }));
    } else {
      return _jsx(TableComponent, _objectSpread(_objectSpread({
        data: data
      }, others), {}, {
        ref: ref
      }));
    }
  });
  WrappedTable.displayName = `withExpandableRow(${TableComponent.displayName || TableComponent.name})`;
  return WrappedTable;
}

export default withExpandableRow;